import { Checkbox, Flex, Image, message, Modal } from 'antd';
import React, { useState } from 'react';
import { CustomButton, CustomParagraph, CustomTitle } from '../../components';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext';
import useAi from '../../assets/images/useAi.svg';
import { dontShowMessage } from '../../apis/shipment';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import emailAi from '../../assets/images/emailAIImage.png';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { lowerLabel } from '@/common/utils/utils';

interface EmailAiAssistantModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (dontShowAgain: boolean) => void;
  setShowEmailModal: (show: boolean) => void;
}

const EmailAiAssistantModal: React.FC<EmailAiAssistantModalProps> = ({
  open,
  onCancel,
  onConfirm,
  setShowEmailModal,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState<boolean>(false);
  const { token, decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { showSpin, hideSpin } = useSpin();
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;
    setDontShowAgain(isChecked);
    const refreshToken = localStorage.getItem('@refreshToken');
    const payload = {
      ai_email_static_message: !isChecked,
      refresh: refreshToken,
    };

    showSpin();

    dontShowMessage(token, payload)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            if (res?.data?.data?.data?.access) {
              localStorage.setItem('@token', res?.data?.data?.data?.access);
            }
            if (res?.data?.data?.data?.refresh) {
              localStorage.setItem(
                '@refreshToken',
                res?.data?.data?.data?.refresh
              );
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        console.error('AI Call API Error:', error);
      })
      .finally(() => {
        hideSpin();
      });
  };
  return (
    <Modal
      className='ai-modal'
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={600}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={useAi}
            alt='AI Icon'
            style={{ width: '20px', height: '20px' }}
          />
          <CustomTitle
            text='Email using AI Assistant'
            fontSize='20px'
            lineHeight='28px'
            fontWeight='700'
            marginBottom='0px'
          />
        </div>
      }
    >
      <Image
        src={emailAi}
        alt='AI email preview'
        preview={false}
        style={{
          width: '100%',
          borderRadius: '8px',
          marginBottom: '20px',
          marginTop: '10px',
        }}
      />

      <CustomParagraph
        color='var(--text-primary)'
        text={`The Zoomlogi AI Assistant will email the party in question on ${
          decodedToken?.company_name
        }'s behalf to resolve the issue. You can review the email exchange at any time in the shipment's live ${lowerLabel(
          themeT('chainOfCustody', theme.themeType)
        )}. You will be CC'd and you can jump in the conversation to stop the AI assistant at any time.`}
        fontSize='12px'
        fontWeight='400'
        lineHeight='20px'
        marginBottom='16px'
      />

      <Flex
        justify='space-between'
        align='center'
        style={{ marginTop: '32px' }}
      >
        <Checkbox
          onChange={handleCheckboxChange}
          style={{ fontWeight: 600, fontSize: '12px' }}
        >
          Don’t show this message again
        </Checkbox>

        <Flex gap='16px'>
          <CustomButton
            onClick={onCancel}
            text='Cancel'
            bgColor='transparent'
            color='black'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
          />

          <CustomButton
            onClick={() => {
              onConfirm(dontShowAgain);
              setShowEmailModal(true);
            }}
            text='Continue'
            bgColor='black'
            color='white'
            height='48px'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            borderRadius='8px'
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default EmailAiAssistantModal;
